import React from "react";
import { Box, Checkbox, useTheme } from "@mui/material";
import { Typography } from "@mui/joy";
import { CheckCircleRounded } from "@mui-symbols-material/w300";
import { CommissioningAttribute } from "../../utils/deriveCommissioningSteps";
import CheckDocuments from "./CheckDocuments";

const CONFIRMED_COLOR = "#599460";

interface Props {
    checks: CommissioningAttribute[];
    checked: Record<string, boolean>;
    onToggle: (index: string, value: boolean) => void;
    disabled?: boolean;
}

/**
 * All commissioning checks (IBM-1045) as one list of cards: number, question
 * (`name`), acceptance criterion (`hint`), reference documents and the
 * confirmation checkbox. The whole card toggles the check; confirmed cards
 * turn green.
 */
const CommissioningChecklist = ({ checks, checked, onToggle, disabled }: Props) => {
    const theme = useTheme();
    return (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
            {checks.map((check, position) => {
                const isChecked = !!checked[check.index];
                return (
                    <Box
                        component="li"
                        key={check.index}
                        data-testid={`commissioning-step-${check.index}`}
                        sx={{
                            border: `1px solid ${isChecked ? CONFIRMED_COLOR : "#ddd"}`,
                            borderRadius: "6px",
                            backgroundColor: isChecked ? "#f3f8f3" : "#fff",
                            transition: "border-color 120ms, background-color 120ms",
                        }}
                    >
                        <Box
                            component="label"
                            sx={{ display: "flex", alignItems: "flex-start", gap: "12px", p: "12px", cursor: disabled ? "default" : "pointer" }}
                        >
                            <Box sx={{ width: 24, height: 24, flexShrink: 0, mt: "1px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {isChecked ? (
                                    <CheckCircleRounded sx={{ fontSize: 24, color: CONFIRMED_COLOR }} />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {position + 1}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography level="body-md" sx={{ fontWeight: 600 }}>{check.name}</Typography>
                                {check.hint && (
                                    <Typography level="body-sm" sx={{ color: "text.secondary", mt: "2px" }}>
                                        {check.hint}
                                    </Typography>
                                )}
                                <CheckDocuments attributeIndex={check.index} />
                            </Box>
                            <Checkbox
                                checked={isChecked}
                                onChange={(event) => onToggle(check.index, event.target.checked)}
                                disabled={disabled}
                                sx={{ p: 0, mt: "1px", "&.Mui-checked": { color: CONFIRMED_COLOR } }}
                                inputProps={{ "aria-label": check.name, "data-testid": `commissioning-check-${check.index}` } as React.InputHTMLAttributes<HTMLInputElement>}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default CommissioningChecklist;
